import { useEffect, useState } from 'react';
import { FiArrowLeft, FiCheck, FiExternalLink, FiSave, FiUpload } from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createProject, fetchAdminProject, updateProject, uploadMedia } from '../../api/admin';
import styles from './Projects.module.css';

const emptyProject = {
  id: null, name: '', slug: '', label: '', year: String(new Date().getFullYear()), summary: '', overview: '',
  labelZh: '', summaryZh: '', overviewZh: '',
  technologies: [], highlights: [], decisions: [], architecture: [],
  highlightsZh: [], decisionsZh: [], architectureZh: [], coverImage: '', screenshots: [],
  githubUrl: '', demoUrl: '', status: 'DRAFT', sortOrder: 0,
};

const lines = (items = []) => items.join('\n');
const parseLines = (value = '') => value.split('\n').map((item) => item.trim()).filter(Boolean);
const decisionsToText = (items = []) => items.map((item) => `${item.title} | ${item.text}`).join('\n');
const parseDecisions = (value) => parseLines(value).map((line) => {
  const [title, ...text] = line.split('|');
  return { title: title.trim(), text: text.join('|').trim() };
});
const screenshotsToText = (items = []) => items.map((item) => `${item.src || ''} | ${item.alt || ''}`).join('\n');
const parseScreenshots = (value) => parseLines(value).map((line) => {
  const [src, ...alt] = line.split('|');
  return { src: src.trim(), alt: alt.join('|').trim() };
});

const toForm = (project = emptyProject) => ({
  ...emptyProject,
  ...project,
  name: project.name || '',
  slug: project.slug || '',
  label: project.label || '',
  year: project.year || '',
  summary: project.summary || '',
  overview: project.overview || '',
  labelZh: project.labelZh || '',
  summaryZh: project.summaryZh || '',
  overviewZh: project.overviewZh || '',
  technologies: lines(project.technologies || []),
  highlights: lines(project.highlights || []),
  decisions: decisionsToText(project.decisions || []),
  architecture: lines(project.architecture || []),
  highlightsZh: lines(project.highlightsZh || []),
  decisionsZh: decisionsToText(project.decisionsZh || []),
  architectureZh: lines(project.architectureZh || []),
  screenshots: screenshotsToText(project.screenshots || []),
  coverImage: project.coverImage || '',
  githubUrl: project.githubUrl || '',
  demoUrl: project.demoUrl || '',
});

const readData = (response) => {
  if (response.data?.code !== 200) throw new Error(response.data?.message || 'Request failed.');
  return response.data.data;
};

const ProjectEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;
  const [form, setForm] = useState(() => toForm(emptyProject));
  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [contentLanguage, setContentLanguage] = useState('en');

  useEffect(() => {
    if (isNew) return undefined;
    let active = true;
    fetchAdminProject(slug)
      .then((response) => { if (active) setForm(toForm(readData(response))); })
      .catch((requestError) => { if (active) setError(requestError.message || 'Could not load the project.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isNew, slug]);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setSaved(false);
  };

  const upload = async (file, target) => {
    if (!file) return;
    setUploading(target);
    setError('');
    try {
      const asset = readData(await uploadMedia(file, target === 'coverImage' ? 'covers' : 'projects'));
      setForm((current) => target === 'coverImage'
        ? { ...current, coverImage: asset.publicUrl }
        : { ...current, screenshots: `${current.screenshots}${current.screenshots ? '\n' : ''}${asset.publicUrl} | ` });
    } catch (requestError) {
      setError(requestError.message || 'Image upload failed.');
    } finally {
      setUploading('');
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.slug.trim() || !form.summary.trim()) {
      setError('Name, slug and summary are required.');
      return;
    }
    const project = {
      name: form.name.trim(),
      slug: form.slug.trim().toLowerCase().replace(/\s+/g, '-'),
      label: form.label.trim(),
      year: form.year.trim(),
      summary: form.summary.trim(),
      overview: form.overview.trim(),
      labelZh: form.labelZh.trim() || null,
      summaryZh: form.summaryZh.trim() || null,
      overviewZh: form.overviewZh.trim() || null,
      technologies: parseLines(form.technologies),
      highlights: parseLines(form.highlights),
      decisions: parseDecisions(form.decisions),
      architecture: parseLines(form.architecture),
      highlightsZh: parseLines(form.highlightsZh),
      decisionsZh: parseDecisions(form.decisionsZh),
      architectureZh: parseLines(form.architectureZh),
      coverImage: form.coverImage.trim() || null,
      screenshots: parseScreenshots(form.screenshots),
      githubUrl: form.githubUrl.trim() || null,
      demoUrl: form.demoUrl.trim() || null,
      status: form.status,
      sortOrder: Number(form.sortOrder) || 0,
    };

    setSubmitting(true);
    setError('');
    try {
      const savedProject = readData(form.id
        ? await updateProject(form.id, project)
        : await createProject(project));
      setForm(toForm(savedProject));
      setSaved(true);
      if (isNew || savedProject.slug !== slug) {
        navigate(`/admin/projects/${savedProject.slug}/edit`, { replace: true });
      }
    } catch (requestError) {
      setError(requestError.message || 'Could not save the project.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className={styles.emptyState}>Loading project…</div>;

  return (
    <form className={styles.editor} onSubmit={submit}>
      <header className={styles.editorHeader}>
        <div>
          <Link to="/admin/projects" className={styles.backLink}><FiArrowLeft /> Projects</Link>
          <h1>{isNew ? 'New project' : `Edit ${form.name}`}</h1>
          <p>Shape the case study shown on the public portfolio.</p>
        </div>
        <div className={styles.editorActions}>
          {!isNew && form.status === 'PUBLISHED' && <a href={`/projects/${form.slug}`} target="_blank" rel="noreferrer"><FiExternalLink /> Preview</a>}
          <button type="submit" disabled={submitting}><FiSave /> {submitting ? 'Saving…' : 'Save project'}</button>
        </div>
      </header>

      {saved && <div className={styles.successMessage}><FiCheck /> Project saved to the database.</div>}
      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.editorGrid}>
        <div className={styles.formColumn}>
          <section className={styles.formSection}>
            <div className={styles.sectionTitle}><span>01</span><div><h2>Identity</h2><p>How the project appears in lists and URLs.</p></div></div>
            <div className={styles.fieldGrid}>
              <label><span>Name *</span><input name="name" value={form.name} onChange={update} placeholder="CodeCognition" /></label>
              <label><span>Slug *</span><input name="slug" value={form.slug} onChange={update} placeholder="code-cognition" /></label>
              <label><span>Year</span><input name="year" value={form.year} onChange={update} placeholder="2026" /></label>
            </div>
          </section>

          <section className={styles.formSection}>
            <div className={styles.sectionTitle}><span>02</span><div><h2>Localized content</h2><p>Edit English and Chinese independently. Empty Chinese fields fall back to English.</p></div></div>
            <div className={styles.languageTabs} role="tablist" aria-label="Project content language">
              <button type="button" className={contentLanguage === 'en' ? styles.activeLanguage : ''} onClick={() => setContentLanguage('en')}>English</button>
              <button type="button" className={contentLanguage === 'zh' ? styles.activeLanguage : ''} onClick={() => setContentLanguage('zh')}>中文</button>
            </div>
            {contentLanguage === 'en' ? <>
              <label><span>Category label</span><input name="label" value={form.label} onChange={update} placeholder="PERSONAL PORTFOLIO & BLOG" /></label>
              <label><span>Summary *</span><textarea name="summary" value={form.summary} onChange={update} rows="3" /></label>
              <label><span>Overview</span><textarea name="overview" value={form.overview} onChange={update} rows="6" /></label>
              <label><span>Highlights</span><textarea name="highlights" value={form.highlights} onChange={update} rows="6" /></label>
              <label><span>Decisions</span><textarea name="decisions" value={form.decisions} onChange={update} rows="6" placeholder="Keep the stack understandable | The application stays conventional..." /></label>
              <label><span>Architecture nodes</span><textarea name="architecture" value={form.architecture} onChange={update} rows="5" placeholder={'React SPA\nREST API\nSpring Boot\nMySQL'} /></label>
            </> : <>
              <label><span>分类标签</span><input name="labelZh" value={form.labelZh} onChange={update} placeholder="个人作品集与博客" /></label>
              <label><span>摘要</span><textarea name="summaryZh" value={form.summaryZh} onChange={update} rows="3" /></label>
              <label><span>详细介绍</span><textarea name="overviewZh" value={form.overviewZh} onChange={update} rows="6" /></label>
              <label><span>项目亮点</span><textarea name="highlightsZh" value={form.highlightsZh} onChange={update} rows="6" /></label>
              <label><span>技术决策</span><textarea name="decisionsZh" value={form.decisionsZh} onChange={update} rows="6" placeholder="保持技术栈清晰 | 采用常规且容易维护的实现方式..." /></label>
              <label><span>架构节点</span><textarea name="architectureZh" value={form.architectureZh} onChange={update} rows="5" placeholder={'React 单页应用\nREST API\nSpring Boot\nMySQL'} /></label>
            </>}
          </section>

          <section className={styles.formSection}>
            <div className={styles.sectionTitle}><span>03</span><div><h2>Shared details</h2><p>These values are shared by both language versions.</p></div></div>
            <label><span>Technologies</span><textarea name="technologies" value={form.technologies} onChange={update} rows="5" placeholder={'React\nSpring Boot\nMySQL'} /></label>
          </section>

          <section className={styles.formSection}>
            <div className={styles.sectionTitle}><span>04</span><div><h2>Media</h2><p>Upload images to persistent storage on the Oracle server.</p></div></div>
            <label><span>Cover image URL</span><input name="coverImage" value={form.coverImage} onChange={update} placeholder="https://.../cover.webp" /></label>
            <label className={styles.uploadButton}><FiUpload /> {uploading === 'coverImage' ? 'Uploading…' : 'Upload cover'}<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={Boolean(uploading)} onChange={(event) => upload(event.target.files?.[0], 'coverImage')} /></label>
            <label><span>Screenshots</span><textarea name="screenshots" value={form.screenshots} onChange={update} rows="5" placeholder="https://.../screen.webp | Admin project editor" /></label>
            <label className={styles.uploadButton}><FiUpload /> {uploading === 'screenshots' ? 'Uploading…' : 'Upload screenshot'}<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={Boolean(uploading)} onChange={(event) => upload(event.target.files?.[0], 'screenshots')} /></label>
          </section>
        </div>

        <aside className={styles.settingsCard}>
          <p>PROJECT SETTINGS</p>
          <label><span>Status</span><select name="status" value={form.status} onChange={update}><option value="DRAFT">Draft</option><option value="PUBLISHED">Published</option></select></label>
          <label><span>Display order</span><input name="sortOrder" type="number" value={form.sortOrder} onChange={update} /></label>
          <label><span>GitHub URL</span><input name="githubUrl" value={form.githubUrl} onChange={update} placeholder="https://github.com/..." /></label>
          <label><span>Live URL</span><input name="demoUrl" value={form.demoUrl} onChange={update} placeholder="https://code-cognition.com" /></label>
          <div className={styles.statusBlock}><span>Status</span><strong>{form.status}</strong><p>Drafts remain private. Published projects appear on the public portfolio immediately.</p></div>
        </aside>
      </div>
    </form>
  );
};

export default ProjectEditor;
